import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { findMediaUrl, resolveMediaUrl } from "./editorialMedia.mjs";

let mediaIndexPromise;

function loadMediaIndex() {
  if (!mediaIndexPromise) {
    mediaIndexPromise = api.get("/admin/media")
      .then((response) => response.data || [])
      .catch((error) => {
        mediaIndexPromise = undefined;
        throw error;
      });
  }
  return mediaIndexPromise;
}

/**
 * <img> cannot attach the required Bearer token to cross-origin protected
 * media. Load only known platform image endpoints through authenticated Axios,
 * then render an ephemeral object URL. Never put a paid media token into a URL.
 */
export function authorizedEditorialImagePath(url) {
  if (typeof url !== "string" || !url) return null;
  const backend = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
  // Accept only same-region API media paths. Old cross-region addresses must
  // never be bundled into RU or used as an image src / bearer-token target.
  const path = url.startsWith("/api/")
    ? url
    : backend && url.startsWith(`${backend}/api/`)
      ? url.slice(backend.length) : "";
  if (!path) return null;
  if (/^\/api\/media\/files\/[0-9a-f]{24}$/i.test(path) ||
      /^\/api\/book\/[^/?]+\/pages\/[^/?]+\/intro-image\/(?:hero|hero_mobile)$/.test(path) ||
      /^\/api\/book\/[^/?]+\/pages\/[^/?]+\/spread-images\/[^/?]+\/\d+$/.test(path)) {
    return path.slice(4);
  }
  return null;
}

export function useEditorialMediaUrl(url, mediaId, { publicReader = false } = {}) {
  const [indexedUrl, setIndexedUrl] = useState("");
  const directUrl = resolveMediaUrl(url);
  const sourceUrl = directUrl || indexedUrl;
  const imagePath = authorizedEditorialImagePath(sourceUrl);
  // Only public Open Library originals use direct URLs; Book images remain protected.
  const publicImage = publicReader && /^\/media\/files\/[0-9a-f]{24}$/i.test(imagePath || "");
  const protectedPath = publicImage ? null : imagePath;
  const foreignApiMedia = /^https?:\/\/[^/]+\/api\/(?:media\/files\/|book\/)/i.test(sourceUrl) && !imagePath;
  const safeSourceUrl = foreignApiMedia ? "" : sourceUrl;
  const [resolvedUrl, setResolvedUrl] = useState(protectedPath ? "" : safeSourceUrl);

  // Resolve an ID-only media binding to its original URL first. It then enters
  // the same authenticated blob path as an explicit URL, never a raw <img> src.
  useEffect(() => {
    let active = true;
    setIndexedUrl("");
    if (!directUrl && mediaId && !publicReader) {
      loadMediaIndex()
        .then((items) => {
          if (active) setIndexedUrl(findMediaUrl(items, mediaId));
        })
        .catch(() => {
          if (active) setIndexedUrl("");
        });
    }
    return () => { active = false; };
  }, [directUrl, mediaId, publicReader]);

  useEffect(() => {
    let active = true;
    let objectUrl = null;

    if (protectedPath) {
      setResolvedUrl("");
      api.get(protectedPath, { responseType: "blob" })
        .then((response) => {
          if (!active) return;
          if (!response.data?.type?.startsWith("image/")) return;
          objectUrl = URL.createObjectURL(response.data);
          setResolvedUrl(objectUrl);
        })
        .catch(() => {
          if (active) setResolvedUrl("");
        });
      return () => {
        active = false;
        if (objectUrl) URL.revokeObjectURL(objectUrl);
      };
    }

    setResolvedUrl(safeSourceUrl);
    return () => { active = false; };
  }, [safeSourceUrl, protectedPath]);

  return resolvedUrl;
}
